import { useTranslation } from "react-i18next";
import { ButtonTypes } from "../../Definitions";
import { colors, fonts, glyphs, mediaQueriesNumbers, useMediaQuery } from "../../Styles";
import { NewButton, TableButton } from "../BaseComponents";
import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import { TablePrefs } from "./subComponents/tablePrefsWidget";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../Store";
import { SET_EDIT_MODE } from "../../Store/UI/editModeSlice";

/* eslint-disable */
export enum TableTypes {
  fullPage = "fullPage",
  fitContent = "fitContent"
}
/* eslint-enable */

interface TableLayoutProps {
  zIndex?: number
  tableType?: TableTypes;
  searchbBarArea?: JSX.Element;
  filterDisplayListArea?: JSX.Element;
  buttonArea?: JSX.Element;
  tableArea?: JSX.Element;
  additionalArea?: JSX.Element;
  paginationBtns?: {
    onNext: () => void;
    onPrev: () => void;
    canPreviousPage: boolean;
    canNextPage: boolean;
    thisPageIndex: number;
    pageCount: number;
  };
  editableTable?: boolean;
  hideOnMobile?: boolean;
  fillScreenTable?: boolean
}

export default function TableLayout({ tableType = TableTypes.fullPage, searchbBarArea, filterDisplayListArea, buttonArea, tableArea, additionalArea, paginationBtns, editableTable = false, zIndex, hideOnMobile = false, fillScreenTable }: TableLayoutProps) {
  const { t } = useTranslation();
  const [fillScreen, setFillScreen] = useState<boolean>(false);
  const [tablePrefsState, setTablePrefsState] = useState<boolean>(false); 
  const tablePrefsRef = useRef<HTMLDivElement | null>(null);
  const tableHeightRef = useRef<HTMLDivElement | null>(null);
  const isEditableTable: boolean = editableTable && editableTable === true
  const mobileXs = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileXs}px)`);
  const mobile = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileMax}px)`);
  const popoutTablePrefs = useMediaQuery(`(max-width: 1000px)`);
  const paginationButtonsPosition = useMediaQuery(`(max-width: 1000px)`);
  const dispatchEditMode = useDispatch();
  const editMode = useSelector((state: RootState) => state.uiState.editMode.editMode);

  const _gridTemplateRows =
  tableType === TableTypes.fitContent
    ? paginationBtns
      ? "50px fit-content(100%) 40px"
      : "50px fit-content(100%)"
    : paginationBtns
      ? "50px 1fr 40px"
        : "50px 1fr";
  
  const tablePrefs = ( tableHeightRef.current && additionalArea && 
    <TablePrefs content={additionalArea} popout={popoutTablePrefs} tableHeight={mobile ? "100%" : tableType === TableTypes.fullPage ? "100%" : `${tableHeightRef.current.offsetHeight}px`}/>
  )

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        tablePrefsState &&
        tablePrefsRef.current &&
        !tablePrefsRef.current.contains(event.target as Node)
      ) {
        setTablePrefsState(false);
      }
    }
  
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [tablePrefsState]);

  const paginationText =  paginationBtns && <p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyLightTertiary }}>
    {`${t("page", { defaultValue: "page" })} ${ paginationBtns.thisPageIndex} ${t("of", { defaultValue: "of" })} ${ paginationBtns.pageCount}`}
  </p>

  const paginationButtons = paginationBtns && <div style={{ display: "flex", alignItems: "center", justifyContent: paginationButtonsPosition ? "center" : "flex-start", gap: paginationButtonsPosition ? "10px" : "5px", padding: "5px 20px 0px 20px", zIndex: zIndex ? zIndex : 0 }}>
    <NewButton
      content={""}
      buttonType={ButtonTypes.arrowButton}
      onClick={paginationBtns.onPrev}
      startEnhancer={glyphs.Arrow({ rotate: 180 })}
      overrides={{
        baseStyles: { backgroundColor: colors.greyPrimary },
        hoverStyles: { backgroundColor: colors.greySecondary },
      }}
      disabled={isEditableTable || paginationBtns.canPreviousPage}
    />
    {paginationButtonsPosition && paginationText }
    <NewButton
      content={""}
      buttonType={ButtonTypes.arrowButton}
      onClick={paginationBtns.onNext}
      startEnhancer={glyphs.Arrow({})}
      overrides={{
        baseStyles: { backgroundColor: colors.greyPrimary },
        hoverStyles: { backgroundColor: colors.greySecondary },
      }}
      disabled={isEditableTable || paginationBtns.canNextPage}
    />
    {!paginationButtonsPosition && paginationText }
  </div>
  
  return (<>
    <div style={{ height: "100%", width: "100%", display: "grid", gridTemplateRows: _gridTemplateRows, boxSizing: "border-box", position: "relative" }}>

      {/* Top Area */}
      <div style={{ gridArea: "1/1/2/2", boxSizing: "border-box", width: "100%", padding: "0px 20px", display: "grid", gridTemplateColumns: mobile ? "1fr" : isEditableTable ? "280px 1fr 390px" : "280px 1fr 272px", gridTemplateRows: "50px" }}>
        {mobileXs && hideOnMobile ? undefined : searchbBarArea && <div style={{ marginBottom: "5px", gridArea: "1/1/2/2", display: "flex", alignItems: "end" }}>
          {searchbBarArea}
        </div>}

        {!mobile && filterDisplayListArea && <div style={{ gridArea: "1/2/2/3", display: "flex", alignItems: "end", position: "relative" }}>
          {filterDisplayListArea}
        </div>}

        {buttonArea && <div style={{ display: "flex", justifyContent: "end", gap: "5px", gridArea: "1/3/2/4", zIndex: zIndex ? zIndex : 1 /* at least 1 to stay on top of table */}}>
          {buttonArea}
        </div>}
      </div>

      {/* Table Area */}
      {tableArea && !fillScreen && <div ref={tableHeightRef} style={{ gridArea: "2/1/3/2", height: "100%", boxSizing: "border-box", position: "relative", backgroundColor: colors.greyLightSecondary, border: "5px solid " + colors.greyPrimary, borderRadius: "10px", overflow: "hidden", zIndex: zIndex ? zIndex : 0 }}>
        {tableArea}
      </div>}

      {/* Blue Print */}
      {popoutTablePrefs && additionalArea && editableTable && (
        <div ref={tablePrefsRef} style={{zIndex: zIndex ? zIndex : 1, position: "absolute", transition: "all ease-in-out 0.5s", left: tablePrefsState ? mobileXs ? "0px" : "10px" : "-310px", bottom: tableType === TableTypes.fullPage ? "100px" : undefined}}>
          <TableButton
            content={t("animalPages.animals.tablePreferences", { defaultValue: "Table preferences" })}
            disabled={false}
            onClick={() => {setTablePrefsState(!tablePrefsState)}}
            startEnhancer={tablePrefsState ? glyphs.Close({}) : glyphs.Arrow({})}
            bottomPosition={220}
            leftPosition={305}
          />
          {tablePrefs}
        </div>
      )}

      { /* Additional Area */}
      {!popoutTablePrefs && additionalArea && editableTable && createPortal(
         <div style={{ zIndex: zIndex ? zIndex : 1, position: "absolute", top: "210px", bottom: "125px", left: "20px" }}>{tablePrefs}</div>,
      document.body)}
      
      {/* Pagination Buttons Area */}
      <div style={{ gridArea: "3/1/4/2"}}>
      { !fillScreen && paginationButtons}
      </div>

      {!isEditableTable && fillScreenTable && mobileXs &&
        <TableButton
          content={t("systemControls.blueprint.maximize", { defaultValue: "maximize" })}
          disabled={false}
          onClick={() => { setFillScreen(true); dispatchEditMode(SET_EDIT_MODE(true)) } }
          startEnhancer={glyphs.Maximize({})}
          gridArea="2/1/3/2"
          fitTable
        />
      }

    </div>
    {tableArea && editMode && fillScreen && mobileXs &&
      <div style={{ 
        backgroundColor: colors.blackPrimary+"cc", 
        zIndex: 9999,
        position: "absolute", 
        top: 0, 
        left: 0, 
        bottom: 0, 
        right: 0, 
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gridTemplateRows: paginationBtns ? "1fr 40px" : "1fr", height: "98%", width: "95%" }}>
          <div style={{ gridArea: "1/1/2/2", boxSizing: "border-box", position: "relative", backgroundColor: colors.greyLightSecondary, border: "5px solid " + colors.greyPrimary, borderRadius: "10px", overflow: "hidden" }}>
            {tableArea}
            <TableButton
              content={t("systemControls.blueprint.minimize", { defaultValue: "minimize" })}
              disabled={false}
              onClick={() => { setFillScreen(false); dispatchEditMode(SET_EDIT_MODE(false)) } }
              startEnhancer={glyphs.Minimize({})}
              fitTable
            />
          </div>
          {paginationBtns && paginationButtons}
        </div>
      </div>
    }
  </>)
}